<script lang="ts" setup>
    import { computed, nextTick, onMounted, ref, watch } from 'vue';
    import { useValidationErrors } from '@/js/composables/useValidationErrors';
    import ErrorMessage from './ErrorMessage.vue';

    const model = defineModel<string>();

    const props = withDefaults(
        defineProps<{
            id?: string;
            autoResize?: boolean;
            rows?: number;
            invalid?: boolean;
            validationKey?: string;
            scope?: string;
        }>(),
        {
            id: undefined,
            autoResize: true,
            rows: 2,
            invalid: undefined,
            validationKey: undefined,
            scope: undefined,
        }
    );

    const textarea = ref<HTMLTextAreaElement | null>(null);

    const v = props.validationKey ? useValidationErrors(props.scope) : null;

    const effectiveInvalid = computed(() =>
        props.invalid !== undefined
            ? props.invalid
            : !!(v && props.validationKey && v.hasError(props.validationKey))
    );

    const effectiveErrorId = computed(() =>
        v && props.validationKey ? v.errorId(props.validationKey) : undefined
    );

    const resize = () => {
        if (props.autoResize && textarea.value) {
            textarea.value.style.height = 'auto';
            textarea.value.style.height = `${textarea.value.scrollHeight + 2}px`;
        }
    };

    const classes = computed(() => {
        return [
            'TextareaInput',
            props.autoResize ? 'resize-none' : 'resize-y',
            { 'TextareaInput--invalid': effectiveInvalid.value },
        ];
    });

    watch(model, () => {
        if (props.validationKey) v?.clearField(props.validationKey);
    });

    defineOptions({
        inheritAttrs: false,
    });

    onMounted(() => {
        nextTick(resize);
    });

    defineExpose({ textarea });
</script>

<template>
    <textarea
        :id="id"
        ref="textarea"
        v-model="model"
        v-bind="$attrs"
        :class="classes"
        :rows="props.rows"
        :aria-invalid="effectiveInvalid || undefined"
        :aria-describedby="effectiveErrorId"
        @input="resize"
    />
    <ErrorMessage v-if="validationKey" :validation-key="validationKey" :scope="scope" />
</template>
